// Hoy: lo que el agente tiene que hacer ahora, en orden de urgencia, y como va su dia.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { cambiarEstadoTarea, llamadas as llamadasCrm } from '../api/crm';
import { useSession } from '../auth/session';
import { useEtapas, useLlamadasRecientes, useOportunidades, useTareas } from '../data/queries';
import { enVentana, kpis, porDevolver, porTipificar, seguimientosDeHoy, serieHoy, tareasAlDia, ventanaHoy, type PeriodoHoy } from '../data/selectors';
import { usePhone } from '../hooks';
import { ago, capitalize, dueLabel, duration, greeting, money } from '../lib/format';
import { Icon } from '../lib/icons';
import { formatPhone } from '../lib/phone-number';
import { phone } from '../phone/store';
import { Empty, ErrorBox, IconDot, Pill, Skeleton } from '../ui/bits';
import { Anillos, LeyendaAnillos, type Anillo } from '../ui/graficas/Anillos';
import { BarrasV } from '../ui/graficas/Barras';
import { KpiR } from '../ui/graficas/Cifra';
import { F, SERIE } from '../ui/graficas/formato';
import { senalDe } from '../lib/senalesCaso';
import { useCasosSinCerrar } from './bandeja/useVistas';
import { abrirDialogo } from '../ui/forms';
import { CitaTarjeta, useCitasSemana } from './agendaComun';
import { inicioSemana } from '../api/citas';
import { toast } from '../ui/toasts';

const DIA = 86_400_000;
const PERIODOS: [PeriodoHoy, string][] = [
  ['vivo', 'En vivo'],
  [7, '7 d'],
  [30, '30 d'],
  [365, '12 m'],
];

// Que significa cada eje de la grafica de llamadas, segun el periodo.
const EJE_X: Record<string, string> = {
  vivo: 'horas de hoy, cada barra es una hora',
  7: 'los últimos 7 días, cada barra es un día',
  30: 'los últimos 30 días, cada barra es un día',
  365: 'los últimos 12 meses, cada barra es un mes',
};
const UNIDAD: Record<string, string> = { vivo: 'por hora', 7: 'por día', 30: 'por día', 365: 'por mes' };
const TOPE: Record<string, number> = { 30: 3000, 365: 5000 };

// Llamadas de periodos largos: solo se piden al elegir 30 d o 12 m (los ultimos 14 dias ya estan en cache).
function useLlamadasPeriodo(p: PeriodoHoy) {
  const largo = p === 30 || p === 365;
  return useQuery({
    queryKey: ['llamadas', 'hoy-periodo', p],
    queryFn: () => (p === 365 ? llamadasCrm({ desde: new Date(ventanaHoy(365).desde), max: TOPE[365] }) : llamadasCrm({ desde: new Date(Date.now() - 60 * DIA), max: TOPE[30] })),
    enabled: largo,
    staleTime: p === 365 ? 10 * 60_000 : 60_000,
  });
}

export function Hoy() {
  const { yo, licencia } = useSession();
  const s = usePhone();
  const llamadas = useLlamadasRecientes();
  const tareas = useTareas();
  const opps = useOportunidades();
  const etapas = useEtapas();
  const nav = useNavigate();
  const qc = useQueryClient();
  const tieneExt = !!s.config.extension && s.config.modo !== 'demo';
  const [alcance, setAlcance] = useState<'mias' | 'equipo'>(tieneExt ? 'mias' : 'equipo');
  const ext = alcance === 'mias' && tieneExt ? s.config.extension : undefined;

  // Siempre abre en 7 dias (pedido de Johan, 26-oct): el periodo elegido no se recuerda entre visitas.
  const [periodo, setPeriodo] = useState<PeriodoHoy>(7);
  const largas = useLlamadasPeriodo(periodo);
  const esLargo = periodo === 30 || periodo === 365;
  const fuentePeriodo = esLargo ? largas : llamadas;
  // "En vivo" recalcula la ventana con cada refresco de las llamadas (cada 30 s) y con un reloj de 1 min.
  const [reloj, setReloj] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setReloj(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const ahora = Math.max(reloj, fuentePeriodo.dataUpdatedAt || 0);

  const todas = llamadas.data ?? [];
  const delPeriodo = useMemo(() => {
    const ls = fuentePeriodo.data ?? [];
    return ext ? ls.filter((l) => l.agente === ext) : ls;
  }, [fuentePeriodo.data, ext]);
  const ventana = ventanaHoy(periodo, ahora);
  const actuales = useMemo(() => enVentana(delPeriodo, ventana.desde, ventana.hasta), [delPeriodo, ventana.desde, ventana.hasta]);
  const k = kpis(actuales);
  const kAntes = ventana.antes ? kpis(enVentana(delPeriodo, ventana.antes[0], ventana.antes[1])) : null;
  const serie = useMemo(() => serieHoy(delPeriodo, periodo, ahora), [delPeriodo, periodo, ahora]);
  const cargandoPeriodo = fuentePeriodo.isLoading;
  const truncado = esLargo && (largas.data?.length ?? 0) >= TOPE[String(periodo)];
  const pend = useMemo(() => porTipificar(todas, { extension: ext }), [todas, ext]);
  const dev = useMemo(() => porDevolver(todas), [todas]);
  const seg = useMemo(() => seguimientosDeHoy(tareas.data ?? [], { miembroId: yo.miembroId }), [tareas.data, yo.miembroId]);
  const ganada = etapas.data?.[etapas.data.length - 1]?.valor;
  const porCerrar = (opps.data ?? [])
    .filter((o) => o.etapa !== ganada)
    .sort((a, b) => (a.cierre ? new Date(a.cierre).getTime() : Infinity) - (b.cierre ? new Date(b.cierre).getTime() : Infinity) || b.monto - a.monto)
    .slice(0, 5);

  const fecha = capitalize(new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' }));
  const nombre = yo.nombre.split(' ')[0];
  const totalPend = dev.length + pend.length + seg.length;

  // Avance: anillos con datos reales (los dos primeros son del periodo; tareas y casos, de ahora).
  const casos = useCasosSinCerrar();
  const [foco, setFoco] = useState<string | null>(null);
  const tal = tareasAlDia(tareas.data ?? [], { miembroId: alcance === 'mias' ? yo.miembroId : undefined });
  const casosAhora = useMemo(() => {
    if (!casos.data) return null;
    const lista = casos.data.filter((c) => alcance !== 'mias' || !yo.miembroId || c.asignadoId === yo.miembroId);
    const aTiempo = lista.filter((c) => senalDe(c) !== 'espera').length;
    return { total: lista.length, aTiempo };
  }, [casos.data, alcance, yo.miembroId]);
  const anillos: Anillo[] = [
    { k: 'contestadas', nombre: 'Llamadas contestadas', pct: k.total ? (100 * k.contestadas) / k.total : 0, color: SERIE[0], detalle: k.total ? `${F.n(k.contestadas)} de ${F.n(k.total)} llamadas` : 'Sin llamadas en el periodo' },
    { k: 'tipificadas', nombre: 'Llamadas tipificadas', pct: k.contestadas ? k.tipificadasPct : 0, color: SERIE[1], detalle: k.contestadas ? `de ${F.n(k.contestadas)} contestadas` : 'Sin llamadas contestadas' },
    { k: 'tareas', nombre: 'Tareas al día', pct: tal.pct, color: SERIE[4], detalle: tal.total ? `${tal.alDia} de ${tal.total} sin vencer · ahora` : 'Sin tareas pendientes' },
    ...(casosAhora
      ? [{ k: 'casos', nombre: 'Casos dentro de tiempo', pct: casosAhora.total ? (100 * casosAhora.aTiempo) / casosAhora.total : 100, color: SERIE[3], detalle: casosAhora.total ? `${casosAhora.aTiempo} de ${casosAhora.total} abiertos · ahora` : 'Sin casos abiertos' }]
      : []),
  ];
  const etiquetaPeriodo = PERIODOS.find(([p]) => p === periodo)?.[1] ?? '';
  const nombrePeriodo = periodo === 'vivo' ? 'hoy' : periodo === 365 ? 'en 12 meses' : `en ${periodo} días`;

  const completar = async (id: string) => {
    try {
      await cambiarEstadoTarea(id, 'DONE');
      void qc.invalidateQueries({ queryKey: ['tareas'] });
      toast('Tarea completada.');
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };

  return (
    <div className="view">
      <header className="vhead">
        <div>
          <h1>
            {greeting()}
            {yo.miembroId ? `, ${nombre}` : ''}
          </h1>
          <p className="sub">
            {fecha}
            {tieneExt ? ` · Ext ${s.config.extension}` : ''}
            {licencia?.plan ? ` · Plan ${licencia.plan.charAt(0) + licencia.plan.slice(1).toLowerCase().replace('_', ' ')}` : ''}
          </p>
        </div>
        <div className="vh-act">
          <div className="seg hoy-periodo" role="group" aria-label="Periodo de los indicadores">
            {PERIODOS.map(([p, t]) => (
              <button key={String(p)} type="button" aria-pressed={periodo === p} onClick={() => setPeriodo(p)}>
                {p === 'vivo' ? <span className="pd late" aria-hidden="true" /> : null}
                {t}
              </button>
            ))}
          </div>
          {tieneExt ? (
            <div className="seg" role="group" aria-label="Qué llamadas ver">
              <button aria-pressed={alcance === 'mias'} onClick={() => setAlcance('mias')}>
                Mías
              </button>
              <button aria-pressed={alcance === 'equipo'} onClick={() => setAlcance('equipo')}>
                Equipo
              </button>
            </div>
          ) : null}
        </div>
      </header>

      {llamadas.error ? <ErrorBox error={llamadas.error} onRetry={() => llamadas.refetch()} /> : null}

      {largas.error && esLargo ? <ErrorBox error={largas.error} onRetry={() => largas.refetch()} /> : null}

      {cargandoPeriodo ? (
        <section className="kpis" aria-label="Indicadores del periodo" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div className="kpi" key={i}>
              <Skeleton rows={2} />
            </div>
          ))}
        </section>
      ) : (
        <section className="kpis hoy-kpis" aria-label={`Indicadores ${nombrePeriodo}`}>
          <KpiR label={periodo === 'vivo' ? 'Llamadas hoy' : `Llamadas ${nombrePeriodo}`} valor={k.total} fmt={F.n} anterior={kAntes?.total} detalle={kAntes ? undefined : `${F.n(k.contestadas)} contestadas`} />
          <KpiR label="Tiempo medio" valor={k.tiempoMedio} fmt={(v) => (k.contestadas ? duration(v) : '—')} anterior={kAntes?.contestadas ? kAntes.tiempoMedio : null} detalle={kAntes?.contestadas ? undefined : `${F.n(k.minutos)} min hablados`} />
          <KpiR label="Tipificadas" valor={k.tipificadasPct} fmt={(v) => (k.contestadas ? `${Math.round(v)}%` : '—')} anterior={kAntes?.contestadas ? kAntes.tipificadasPct : null} detalle={kAntes?.contestadas ? undefined : `${pend.length} pendientes`} />
          <KpiR
            label="Perdidas"
            valor={k.perdidas}
            fmt={F.n}
            anterior={kAntes?.perdidas || null}
            menosEsMejor
            detalle={<span className={dev.length ? 'dn' : 'up'}>{dev.length} por devolver</span>}
            tono={k.total && k.perdidas / k.total > 0.2 ? 'warn' : undefined}
          />
        </section>
      )}

      <div className="hoy-periodos" key={String(periodo)}>
        <section className="box hoy-avance" aria-labelledby="h-avance">
          <div className="box-h">
            <div>
              <h2 className="lbl" id="h-avance">
                Avance
              </h2>
              <p className="sub">% de cada meta · {nombrePeriodo}</p>
            </div>
            <Pill tone="acc">{etiquetaPeriodo}</Pill>
          </div>
          <div className="an-par">
            <Anillos items={anillos} centro={k.total} sub={k.total === 1 ? 'llamada' : 'llamadas'} foco={foco} />
            <LeyendaAnillos items={anillos} onFoco={setFoco} />
          </div>
        </section>
        <section className="box hoy-grafica" aria-labelledby="h-graf">
          <div className="box-h">
            <div>
              <h2 className="lbl" id="h-graf">
                {ext ? 'Tus llamadas' : 'Llamadas del equipo'} {UNIDAD[String(periodo)]}
              </h2>
              <p className="sub">
                <span className="ley-inline">
                  <i /> Todas
                </span>{' '}
                <span className="ley-inline">
                  <i className="c2" /> Contestadas
                </span>
                {truncado ? ' · solo las más recientes' : ''}
              </p>
            </div>
            <Pill>{periodo === 'vivo' ? 'Hoy' : etiquetaPeriodo}</Pill>
          </div>
          {cargandoPeriodo ? (
            <Skeleton rows={4} />
          ) : (
            <>
              <BarrasV valores={serie.total} serie2={serie.contestadas} etiquetas={serie.etiquetas} nombres={['Llamadas', 'Contestadas']} alto={210} />
              <p className="ejes">
                <span>
                  ↔ <b>Horizontal:</b> {EJE_X[String(periodo)]}
                </span>
                <span>
                  ↕ <b>Vertical:</b> llamadas {UNIDAD[String(periodo)]} (todas y contestadas)
                </span>
              </p>
            </>
          )}
        </section>
      </div>

      <div className="cols2">
        <section className="box" aria-labelledby="h-sigue">
          <div className="box-h">
            <h2 className="lbl" id="h-sigue">
              Qué sigue
            </h2>
            <Pill>{totalPend} pendientes</Pill>
          </div>
          {llamadas.isLoading || tareas.isLoading ? (
            <Skeleton rows={5} />
          ) : totalPend === 0 ? (
            <Empty icon="check" titulo="Estás al día" texto="No hay llamadas por devolver, ni por tipificar, ni seguimientos para hoy." />
          ) : (
            <div className="rows">
              {dev.map((l) => (
                <div className="row act-row" key={l.id}>
                  <IconDot name="phoneMiss" tone="bad" />
                  <div style={{ minWidth: 0 }}>
                    <div className="nm">Devolver llamada a {l.persona?.nombre || formatPhone(l.numero)}</div>
                    <div className="sb">
                      Perdida {ago(l.inicio)} · {l.empresa?.nombre || formatPhone(l.numero)}
                    </div>
                  </div>
                  <button className="btn ok" data-tel={l.numero} onClick={() => phone.dial(l.numero, l.persona ? { id: l.persona.id, nombre: l.persona.nombre, empresa: l.empresa?.nombre } : null)}>
                    <Icon name="phone" />
                    Llamar
                  </button>
                </div>
              ))}
              {pend.slice(0, 5).map((l) => (
                <div className="row act-row" key={l.id}>
                  <IconDot name="tag" tone="warn" />
                  <div style={{ minWidth: 0 }}>
                    <div className="nm">Tipificar llamada con {l.persona?.nombre || formatPhone(l.numero)}</div>
                    <div className="sb">
                      {l.direccion === 'ENTRANTE' ? 'Entrante' : 'Saliente'} · {duration(l.duracion)} · {ago(l.inicio)}
                    </div>
                  </div>
                  <button
                    className="btn"
                    onClick={() =>
                      phone.abrirCierreExistente({
                        llamadaId: l.id,
                        numero: l.numero,
                        contacto: l.persona ? { id: l.persona.id, nombre: l.persona.nombre, empresa: l.empresa?.nombre } : null,
                        dir: l.direccion === 'ENTRANTE' ? 'in' : 'out',
                        duracion: l.duracion,
                        inicio: new Date(l.inicio ?? l.creado).getTime(),
                        notas: l.notas,
                      })
                    }
                  >
                    Tipificar
                  </button>
                </div>
              ))}
              {pend.length > 5 ? (
                <div className="row act-row">
                  <IconDot name="tag" tone="warn" />
                  <div className="sb">Y {pend.length - 5} llamadas más por tipificar.</div>
                  <button className="btn ghost" onClick={() => nav('/llamadas?ver=sintip')}>
                    Ver todas
                  </button>
                </div>
              ) : null}
              {seg.map((t) => (
                <div className="row act-row" key={t.id}>
                  <button className="check" onClick={() => completar(t.id)} aria-label={`Marcar como hecha: ${t.titulo}`}>
                    <Icon name="check" />
                  </button>
                  <button className="tarea-abrir" style={{ minWidth: 0 }} onClick={() => abrirDialogo({ tipo: 'verTarea', id: t.id })} aria-label={`Abrir tarea: ${t.titulo}`}>
                    <div className="nm">{t.titulo}</div>
                    <div className="sb">
                      {t.persona?.nombre ?? 'Sin contacto'} · {dueLabel(t.vence)}
                    </div>
                  </button>
                  <button className="btn ghost" onClick={() => abrirDialogo({ tipo: 'verTarea', id: t.id })}>
                    Abrir
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="stackv">
          <CitasDeHoy />
          <section className="box">
            <div className="box-h">
              <h2 className="lbl">Por cerrar</h2>
              <Link className="btn ghost" to="/oportunidades">
                Ver pipeline
              </Link>
            </div>
            {opps.isLoading ? (
              <Skeleton rows={3} />
            ) : porCerrar.length ? (
              <div className="rows">
                {porCerrar.map((o) => (
                  <button key={o.id} className="row act-row" onClick={() => abrirDialogo({ tipo: 'verOportunidad', id: o.id })}>
                    <IconDot name="building" tone="acc" />
                    <div style={{ minWidth: 0 }}>
                      <div className="nm">{o.nombre}</div>
                      <div className="sb">
                        {o.empresa?.nombre ?? 'Sin empresa'} · {etapas.data?.find((e) => e.valor === o.etapa)?.etiqueta ?? o.etapa}
                        {o.cierre ? ` · cierra ${new Date(o.cierre).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}` : ''}
                      </div>
                    </div>
                    <span className="mono">{money(o.monto, o.moneda)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <Empty titulo="Sin oportunidades abiertas">
                <button className="btn" onClick={() => abrirDialogo({ tipo: 'oportunidad' })}>
                  <Icon name="plus" />
                  Nueva oportunidad
                </button>
              </Empty>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// Citas de hoy (mias o sin responsable); la Agenda completa esta en Inicio -> Agenda.
function CitasDeHoy() {
  const { yo } = useSession();
  const r = useCitasSemana(inicioSemana(new Date()));
  const hoy = new Date().toDateString();
  const citas = (r.data ?? []).filter((c) => new Date(c.inicio).toDateString() === hoy && (!c.responsableId || c.responsableId === yo.miembroId));
  return (
    <section className="box">
      <div className="box-h">
        <h2 className="lbl">Tus citas de hoy</h2>
        <Link to="/agenda" className="btn sm ghost">
          Agenda
        </Link>
      </div>
      {r.isLoading ? (
        <Skeleton rows={2} />
      ) : citas.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {citas.map((c) => (
            <CitaTarjeta key={c.id} c={c} />
          ))}
        </div>
      ) : (
        <p className="note">
          Sin citas hoy.{' '}
          <button className="linkish" onClick={() => abrirDialogo({ tipo: 'cita' })}>
            Agendar una
          </button>
        </p>
      )}
    </section>
  );
}
